import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrameTypesEnum} from '../../../../../libs/shared/core/src/models/api/apiTypes';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {getHasFluSetupGuide} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {entryPointIsInboxSidePanelEntryPoint} from '../../../../models/api/applicationEntryPointModel';
import {StartGuideTaskTypesEnum} from '../../../../models/api/preferencesModel';
import {getApplications} from '../../../../store/data/applications/applicationsSelectors';
import {updateStartGuideTasksAction} from '../../../../store/data/preferences/preferencesActions';
import {getCompletedStartGuideTasks} from '../../../../store/data/preferences/preferencesSelectors';
import {getShouldNonAdminShowStartGuideSuccessScreen} from '../../../../store/stateless/onboardingSelectors';
import {SidePanelFrame} from '../../../../store/stateless/sidePanelFrameSelectors';
import {EntryPointSwitcherItem} from './entryPointSwitcherItem';
import {IconSwitcherItem} from './iconSwitcherItem';
import {IntegrationSwitcherItem} from './integrationSwitcherItem';
import {InternalSwitcherItem} from './internalSwitcherItem';

/*
 * Props.
 */

export interface FrameSwitcherItemSharedProps {
  isUserGlobalAdmin: boolean;
  selectedFrame: SidePanelFrame | undefined;
  onFrameSelect: (frame: SidePanelFrame) => void;
}

interface FrameSwitcherItemProps extends FrameSwitcherItemSharedProps {
  frame: SidePanelFrame;
  index: number;
  isOtherDragging: boolean;
  isInMultiViewButton?: boolean;
  shouldForceRenderPlaceholder?: boolean;
  onDragOver: (frameId: string, overFrameId: string) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

/*
 * Style.
 */

const StyledManageSwitcherItemDiv = styled.div`
  margin: 0px auto;
  height: 2px;
  border-radius: 4px;
  background-color: ${(p) => p.theme.greys.shade30};
  width: 24px;
`;

/*
 * Components.
 */

export const FrameSwitcherItem: FC<FrameSwitcherItemProps> = (props) => {
  const {
    frame,
    index,
    selectedFrame,
    isInMultiViewButton,
    shouldForceRenderPlaceholder,
    onDragOver,
    onFrameSelect,
  } = props;
  const isSelected = frame === selectedFrame;
  const dispatch = useAppDispatch();

  const applications = useAppSelector(getApplications);
  const hasFluSetupGuide = useAppSelector(getHasFluSetupGuide);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const completedStartGuideTasks = useAppSelector(getCompletedStartGuideTasks);
  const shouldShowStartGuideSuccessScreen = useAppSelector(getShouldNonAdminShowStartGuideSuccessScreen);
  const dismissStartGuideSuccessScreen = () => {
    dispatch(updateStartGuideTasksAction(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS));
  };

  // Manager.
  if (frame.frameType === FrameTypesEnum.MANAGER) {
    return renderManagerIntegrationsButton(props);
  }

  if (frame.frameType === FrameTypesEnum.CALENDAR) {
    return (
      <InternalSwitcherItem
        {...props}
        index={index}
        frameId={frame.frameId}
        iconName="calendar"
        hoverIconName="calendar"
        color={PaletteColorsEnum.RED}
        isSelected={isSelected}
        isInMultiViewButton={isInMultiViewButton}
        shouldForceRenderPlaceholder={shouldForceRenderPlaceholder}
        onDragOver={(frameId) => onDragOver(frameId, frame.frameId)}
        onClick={() => onFrameSelect(frame)}
        label={<Trans id="sidepanel-calendar-p4-title">Calendar</Trans>}
        renderTooltip={() => <Trans id="sidepanel-switcher-calendar">Calendar</Trans>}
      />
    );
  }

  if (frame.frameType === FrameTypesEnum.START_GUIDE) {
    // Only render if they are a FLU that hasn't completed the guide yet
    return !hasFluSetupGuide &&
      !isUserGlobalAdmin &&
      !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS)
      ? renderStartGuideIcon(props, shouldShowStartGuideSuccessScreen, dismissStartGuideSuccessScreen)
      : null;
  }

  if (frame.frameType === FrameTypesEnum.KEYBOARD_HELP) {
    return (
      <InternalSwitcherItem
        {...props}
        index={index}
        frameId={frame.frameId}
        iconName="keyboard"
        hoverIconName="keyboard"
        color={PaletteColorsEnum.GREY}
        isSelected={frame === selectedFrame}
        isInMultiViewButton={isInMultiViewButton}
        shouldForceRenderPlaceholder={shouldForceRenderPlaceholder}
        onDragOver={(frameId) => onDragOver(frameId, frame.frameId)}
        onClick={() => onFrameSelect(frame)}
        label={<Trans id="sidepanel-keyboardhelp-p4-title">Shortcuts</Trans>}
        renderTooltip={() => <Trans id="sidepanel-switcher-keyboardhelp">Keyboard Shortcuts</Trans>}
      />
    );
  }

  if (frame.frameType === FrameTypesEnum.CUSTOMER_VIEW) {
    return (
      <InternalSwitcherItem
        {...props}
        interactionId="sidepanel-switch-item-customer-view-btn"
        index={index}
        frameId={frame.frameId}
        iconName="contacts"
        hoverIconName="contacts"
        color={frame.color}
        isSelected={frame === selectedFrame}
        isInMultiViewButton={isInMultiViewButton}
        shouldForceRenderPlaceholder={shouldForceRenderPlaceholder}
        onDragOver={(frameId) => onDragOver(frameId, frame.frameId)}
        onClick={() => onFrameSelect(frame)}
        label={<Trans id="sidepanel-customerview-p4-title">Contacts</Trans>}
        renderTooltip={() => <Trans id="sidepanel-customerview-title">Contact details</Trans>}
      />
    );
  }

  if (frame.frameType === FrameTypesEnum.KNOWLEDGE_BASE) {
    return (
      <InternalSwitcherItem
        {...props}
        interactionId="sidepanel-switch-item-knowledge-base-btn"
        index={index}
        frameId={frame.frameId}
        iconName="knowledgeBaseFilled"
        hoverIconName="knowledgeBaseFilled"
        color={frame.color}
        isSelected={frame === selectedFrame}
        isInMultiViewButton={isInMultiViewButton}
        shouldForceRenderPlaceholder={shouldForceRenderPlaceholder}
        onDragOver={(frameId) => onDragOver(frameId, frame.frameId)}
        onClick={() => onFrameSelect(frame)}
        label={<Trans id="sidepanel-knowledge-base-title">Knowledge</Trans>}
        renderTooltip={() => <Trans id="sidepanel-knowledge-base-tooltip">Knowledge base</Trans>}
      />
    );
  }

  // Integration.
  if (frame.frameType === FrameTypesEnum.INTEGRATION) {
    return (
      <IntegrationSwitcherItem
        {...props}
        index={index}
        frameId={frame.frameId}
        integrationName={frame.name}
        integrationIconsUrl={frame.iconsUrl}
        isSelected={isSelected}
        isInMultiViewButton={isInMultiViewButton}
        shouldForceRenderPlaceholder={shouldForceRenderPlaceholder}
        onDragOver={(frameId) => onDragOver(frameId, frame.frameId)}
        label={frame.name}
        onClick={() => onFrameSelect(frame)}
      />
    );
  }

  if (frame.frameType === FrameTypesEnum.APPLICATION_ENTRY_POINT) {
    if (!entryPointIsInboxSidePanelEntryPoint(frame)) {
      return null;
    }
    const application = applications[frame.applicationId];
    return (
      <EntryPointSwitcherItem
        {...props}
        index={index}
        frameId={frame.frameId}
        name={frame.name}
        color={frame.color}
        isSelected={isSelected}
        iconUrl={application?.iconUrl}
        isInMultiViewButton={isInMultiViewButton}
        shouldForceRenderPlaceholder={shouldForceRenderPlaceholder}
        onDragOver={(frameId) => onDragOver(frameId, frame.frameId)}
        label={frame.name}
        onClick={() => onFrameSelect(frame)}
      />
    );
  }

  // Entry point.
  // TODO: Support displaying an icon when needed.
  return (
    <EntryPointSwitcherItem
      {...props}
      index={index}
      frameId={frame.frameId}
      name={frame.name}
      color={frame.color}
      iconBaseUrl={frame.iconBaseUrl}
      isSelected={isSelected}
      isInMultiViewButton={isInMultiViewButton}
      shouldForceRenderPlaceholder={shouldForceRenderPlaceholder}
      onDragOver={(frameId) => onDragOver(frameId, frame.frameId)}
      label={frame.name}
      onClick={() => onFrameSelect(frame)}
    />
  );
};

/*
 * Helpers.
 */

function renderStartGuideIcon(
  props: FrameSwitcherItemProps,
  shouldRemoveFromSidebar: boolean,
  dismissStartGuide: () => void,
) {
  const {
    frame,
    index,
    selectedFrame,
    onDragOver,
    onFrameSelect,
    isUserGlobalAdmin,
    isInMultiViewButton,
    shouldForceRenderPlaceholder,
  } = props;
  const isSelected = frame === selectedFrame;
  const onStartGuideClick = (iconType: string) => () => {
    trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_EXPAND, {
      user_track: isUserGlobalAdmin ? 'admin' : 'non_admin',
      icon_type: iconType,
      action: isSelected ? 'collapse' : 'expand',
    });
    if (isSelected && shouldRemoveFromSidebar) {
      dismissStartGuide();
    }

    onFrameSelect(frame);
  };
  return (
    <UserThemeConsumer>
      {(theme) => (
        <InternalSwitcherItem
          {...props}
          index={index}
          frameId={frame.frameId}
          iconName="helpFilled"
          hoverIconName="helpFilled"
          selectedIconName="helpFilled"
          color={theme.greys.shade90}
          isSelected={frame === selectedFrame}
          isInMultiViewButton={isInMultiViewButton}
          shouldForceRenderPlaceholder={shouldForceRenderPlaceholder}
          onDragOver={(frameId) => onDragOver(frameId, frame.frameId)}
          onClick={onStartGuideClick('questionMark')}
          label={<Trans id="sidepanel-helpandtips-p4-title">Help &amp; tips</Trans>}
          renderTooltip={() => <Trans id="sidepanel-switcher-helpandtips">Help and Tips</Trans>}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderManagerIntegrationsButton(props: FrameSwitcherItemProps) {
  const {frame, selectedFrame, index, onFrameSelect} = props;
  const isSelected = frame === selectedFrame;

  return (
    <>
      {index !== 0 && <StyledManageSwitcherItemDiv />}
      <IconSwitcherItem
        iconName="channelsFilledSmall"
        isSelected={isSelected}
        onClick={() => onFrameSelect(frame)}
        label={<Trans id="sidepanel-switcher-manage-title">Manage</Trans>}
        renderTooltip={() => <Trans id="sidepanel-switcher-manage">Manage integrations</Trans>}
        isNewIntDiscoveryEnabled
        interactionId="new-integration-discovery-manage-button"
      />
    </>
  );
}
